import React, { useCallback, useMemo, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import toolkitService from '../services/ToolkitService';
import Button from './shared/Button';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import ToolkitFileStep, { STEP, stepIcon } from './toolkit/ToolkitFileStep';
import ToolkitBatchTable from './toolkit/ToolkitBatchTable';
import { useToolkitConfig, ToolkitConfigStep, missingConfig } from './toolkit/ToolkitConfig';
import useToolkitBatch, { sourceRow } from './toolkit/useToolkitBatch';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit › AR Ledger.
//
// Ported from the desktop toolkit's OHIP › AR Ledger: every open invoice in an Opera v5 AR Ledger Aging Detailed
// report (ARAGINGDET XML, or its delimited export) is posted to its Opera Cloud AR account as an OLD BALANCE, through
// the sync project's own OPERA Cloud connection. The desktop's order is kept:
//
//   1. Opera Cloud configuration — the property currency, an active cashier, and AR_BAL_TRX_CODE (the old-balance
//      transaction code, which must exist before anything can post);
//   2. the report, zero balances left out by default;
//   3. validate accounts — each AR account once, not once per invoice; an invoice whose account is not in Opera
//      Cloud is never posted;
//   4. post, row by row, with Pause / Resume.
//
// NEVER TWICE. The server checks the posted-ledger before every post and records the invoice after OHIP accepts it
// (toolkit ar.js postBalance), so re-running a file — after a failure, or by a colleague — skips what is already in
// the ledger. Optional currency conversion posts AROPEN x rate and keeps the original v5 amount in the posting
// remark, as the desktop does.

const COLUMNS = [
    { key: 'ACCOUNT_NO', label: 'AR No' },
    { key: 'ACCOUNT_NAME', label: 'Account' },
    { key: 'INVOICE', label: 'Invoice' },
    { key: 'BILL_NO', label: 'Bill' },
    { key: 'POST_DATE', label: 'Post date' },
    { key: 'GUEST_NAME', label: 'Guest' },
    { key: 'AROPEN', label: 'Open (v5)' },
    { key: 'postedAmount', label: 'Posted' },
];
const SEARCH_KEYS = ['ACCOUNT_NO', 'ACCOUNT_NAME', 'INVOICE', 'GUEST_NAME', 'BILL_NO'];

const errorOf = (err) => err?.response?.data?.message || err?.response?.data?.error?.message || err?.message;

const interpret = (r) => (r.skipped
    ? { status: 'skipped', errorMessage: r.reason === 'Already posted' && r.postedAt ? `Already posted ${new Date(r.postedAt).toLocaleString()}` : r.reason }
    : { status: 'success', postedAmount: `${Number(r.amount).toFixed(2)} ${r.currencyCode}`, errorMessage: null });
const SERVER = { screen: 'ar-ledger', title: 'AR Ledger', keyOf: (row) => `${row.ACCOUNT_NO}|${row.INVOICE_NO || ''}|${row.AROPEN}` };

function ToolkitArLedgerPage() {
    const toolkit = useToolkitProject();
    const { ctx, ready } = toolkit;

    // 1 — Opera Cloud configuration (currency, cashier, AR_BAL_TRX_CODE, optional conversion)
    const config = useToolkitConfig(ctx, { transactionCodeKind: 'ar-transaction-code' });
    const { cashierId, currencyCode, transactionCode, convert, rate, sourceCurrency } = config;

    // 2 — file
    const [excludeZero, setExcludeZero] = useState(true);
    const [skippedZero, setSkippedZero] = useState(0);

    // 3 — account validation, once per account
    const [accounts, setAccounts] = useState(null);       // { [accountNo]: { found, accountId, profileId, error } }
    const [validation, setValidation] = useState({ state: STEP.IDLE, message: '' });

    const canPost = ready && !!accounts && !missingConfig(config).length;

    // The row's one call — or, when its account is not in Opera Cloud, its outcome without one.
    const request = useCallback((row) => {
        const acct = accounts && accounts[row.ACCOUNT_NO];
        if (!acct || !acct.found) {
            return { result: { status: 'failed', errorMessage: acct?.error || 'Account not found in Opera Cloud' } };
        }
        const amount = config.convertAmount(row.AROPEN);
        return { area: 'ar', op: 'post-balance', args: {
            arRecord: sourceRow(row, ['postedAmount']), cashierId, currencyCode, transactionCode,
            accountId: acct.accountId, profileId: acct.profileId,
            ...(convert ? { amountOverride: amount, sourceCurrencyCode: sourceCurrency.trim(), originalArOpen: row.AROPEN, exchangeRate: rate } : {}),
        } };
    }, [accounts, cashierId, currencyCode, transactionCode, convert, rate, sourceCurrency, config]);

    const batch = useToolkitBatch({ request, interpret, ctx, server: SERVER });

    const onParsed = useCallback((rows) => {
        const kept = excludeZero ? rows.filter((r) => parseFloat(r.AROPEN || 0) !== 0) : rows;
        setSkippedZero(rows.length - kept.length);
        setAccounts(null);
        setValidation({ state: STEP.IDLE, message: '' });
        batch.load(kept);
    }, [excludeZero, batch]);

    const uniqueAccounts = useMemo(() => [...new Set(batch.rows.map((r) => r.ACCOUNT_NO))], [batch.rows]);

    const validateAccounts = async () => {
        const found = {};
        setValidation({ state: STEP.BUSY, message: `Validating 0 / ${uniqueAccounts.length} account(s)…` });
        for (let i = 0; i < uniqueAccounts.length; i++) {
            const accountNo = uniqueAccounts[i];
            try {
                const { data } = await toolkitService.ar(ctx, 'validate-account', { accountNo });
                const a = data.result.account;
                const accountId = a?.accountId?.id;
                const profileId = a?.profileId?.id;
                found[accountNo] = !data.result.exists
                    ? { found: false, error: `Account ${accountNo} not found in Opera Cloud` }
                    : (!accountId || !profileId)
                        ? { found: false, error: `Missing account or profile ID for account ${accountNo}` }
                        : { found: true, accountId, profileId };
            } catch (err) {
                found[accountNo] = { found: false, error: errorOf(err) };
            }
            setValidation({ state: STEP.BUSY, message: `Validating ${i + 1} / ${uniqueAccounts.length} account(s)…` });
        }
        const missing = Object.values(found).filter((a) => !a.found).length;
        setAccounts(found);
        setValidation({ state: missing ? STEP.ERROR : STEP.DONE,
            message: `${uniqueAccounts.length - missing} of ${uniqueAccounts.length} account(s) found in Opera Cloud`
                + (missing ? ` — invoices on the ${missing} missing account(s) will not be posted.` : '.') });
    };

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title="AR Ledger"
                subtitle="Post the open invoices of an Opera v5 AR Ledger Aging Detailed report to their Opera Cloud AR accounts as old balances." />

            <ToolkitConfigStep config={config} number={1} ready={ready} />

            <ToolkitFileStep number={2} title="Opera v5 AR Ledger report" label="Choose AR Aging Detailed (XML or CSV)"
                accept=".xml,.rep,.txt,.csv" kind="ar-ledger" onParsed={onParsed} disabled={!ready}>
                <label className="tkb-check" style={{ marginTop: '0.6rem' }}>
                    <input type="checkbox" checked={excludeZero} onChange={(e) => setExcludeZero(e.target.checked)} />
                    <span>Leave out zero balances{skippedZero ? ` (${skippedZero} left out)` : ''}</span>
                </label>
            </ToolkitFileStep>

            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">3</span> Validate accounts {stepIcon(validation.state)}</div>
                <Button variant="secondary" onClick={validateAccounts}
                    disabled={!ready || !batch.rows.length || validation.state === STEP.BUSY || batch.running}>
                    <ShieldCheck className="icon" /> Validate {uniqueAccounts.length || ''} account(s)
                </Button>
                {validation.message && <p className={`tkb-msg${validation.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{validation.message}</p>}
                {!!batch.rows.length && !canPost && (
                    <p className="tkb-hint">Posting needs {missingConfig(config, [!accounts && 'validated accounts']).join(', ')}.</p>
                )}
            </div>

            <ToolkitBatchTable batch={batch} columns={COLUMNS} searchKeys={SEARCH_KEYS} exportName="AR_Ledger"
                canRun={canPost} onClear={() => { batch.load([]); setAccounts(null); setValidation({ state: STEP.IDLE, message: '' }); }} />
        </div>
    );
}

export default ToolkitArLedgerPage;
